import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { IMAGES } from "@/config/site";
import { MaskLines, DonateButton, CtaLink } from "@/components/site/Primitives";

const LunaCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div
      className="relative mt-14 w-full max-w-[340px] lg:absolute lg:bottom-16 lg:right-10 lg:mt-0 xl:right-16"
      style={{ perspective: 900 }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.a
        href="/service-dog-program#meet-luna"
        onMouseMove={onMove}
        onMouseLeave={() => { mx.set(0); my.set(0); }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="flex items-center gap-4 border border-white/15 bg-navy/70 p-4 backdrop-blur-xl"
        data-testid="hero-luna-card"
      >
        <span className="relative block h-20 w-20 shrink-0 overflow-hidden" style={{ transform: "translateZ(40px)" }}>
          <img src={IMAGES.luna.src} alt={IMAGES.luna.alt} className="h-full w-full object-cover" />
        </span>
        <span style={{ transform: "translateZ(25px)" }}>
          <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Ambassador Dog</span>
          <span className="mt-1 block font-display text-xl font-extrabold text-paper">Meet Luna</span>
          <span className="mt-1 block text-xs text-[#C9D4DF]">Our ambassador dog, adopted from Big Dog Ranch Rescue</span>
        </span>
      </motion.a>
    </motion.div>
  );
};

export const HomeHero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, reduce ? 1.02 : 1.1]);
  return (
    <section ref={ref} className="grain relative isolate min-h-[100svh] overflow-hidden bg-navy" data-testid="home-hero">
      <motion.div className="absolute inset-0 -z-10" style={{ y, scale }}>
        <picture>
          <source media="(max-width: 767px)" srcSet={IMAGES.hero.mobile} />
          <img src={IMAGES.hero.src} alt={IMAGES.hero.alt} className="h-full w-full object-cover" style={{ objectPosition: IMAGES.hero.position }} fetchPriority="high" />
        </picture>
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy/80 via-navy/50 to-navy/10" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-2/3 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent md:h-1/2" />
      <div className="container-x relative flex min-h-[100svh] flex-col justify-center pb-16 pt-36">
        <motion.p className="eyebrow eyebrow-light" initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          First Responders Foundation · South Florida
        </motion.p>
        <h1 className="mt-7 font-display text-[2.6rem] font-black uppercase leading-[0.98] tracking-tight text-paper [text-shadow:0_2px_24px_rgba(11,27,45,0.45)] sm:text-6xl lg:text-[5.6rem]" data-testid="hero-headline">
          <MaskLines lines={[{ text: "Two Lives." }, { text: "One Mission." }, { text: "Endless Hope.", className: "text-gold" }]} />
        </h1>
        <motion.div initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }} className="max-w-2xl [text-shadow:0_1px_12px_rgba(11,27,45,0.55)]">
          <p className="mt-8 font-display text-base font-semibold text-paper md:text-lg">
            Supporting First Responders and Veterans Through the Healing Power of Dogs.
          </p>
          <p className="mt-5 text-sm leading-relaxed text-[#D5DEE7] md:text-base">
            First Responders Foundation is dedicated to supporting those who serve our communities through meaningful programs that promote connection, resilience, and well-being. From our Service Dog Program to emergency responder support through Canteen 33, we believe those who dedicate their lives to helping others deserve support in return.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <DonateButton label="Support Our Mission" testId="hero-support-mission-button" />
            <CtaLink to="/service-dog-program" variant="btn-gold" testId="hero-explore-program-button">Explore Our Service Dog Program</CtaLink>
            <CtaLink to="/service-dog-program#apply" variant="btn-ghost-light" testId="hero-apply-button">Apply for the Program</CtaLink>
          </div>
        </motion.div>
        <LunaCard />
      </div>
      <span className="absolute bottom-4 left-5 hidden text-[11px] text-[#D5DEE7] [text-shadow:0_1px_6px_rgba(0,0,0,0.6)] lg:block" data-testid="hero-photo-caption">{IMAGES.hero.caption}</span>
    </section>
  );
};
